import { CommonModule, Location } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { template } from 'lodash';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';

import { RiskModel, RiskSelectionContext } from '@amfam/policy/models';
import { QuickQuoteFeatureFacade } from '@amfam/policy/replace-vehicle/feature';
import { PolicySharedModule } from '@amfam/policy/shared';
import {
  NewVehicle,
  VechiclePolicyChangeEntryPoint,
  VehicleFacade,
  VehicleService
} from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { AgentInfo, PolicyChangeRequestType, State } from '@amfam/shared/models';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DsModalService, Option, UiKitModule, ValidationService } from '@amfam/ui-kit';

import { ReplaceVehiclePolicySelectAction } from '../../../core/store/replace-vehicle-policy/replace-vehicle-policy.actions';
import { SetVehicleRiskSelectionContext } from '../../../core/store/replace-vehicle/replace-vehicle.actions';
import { ReplaceVehicleService } from '../../../core/store/replace-vehicle/replace-vehicle.service';
import { ChangeRequest, RequestChange, VinChange } from '../../models';

@Component({
  selector: 'ds-change-request-vin',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, UiKitModule, PolicySharedModule],
  templateUrl: './change-request-vin.component.html',
  styleUrl: './change-request-vin.component.scss'
})
export class ChangeRequestVinComponent implements OnInit, OnChanges {
  @Input() vehicles: RiskModel[];
  @Input() vinInfo: Partial<RequestChange>;
  @Input() agents: AgentInfo[];
  @Input() years: string[];
  @Input() isPartner: boolean;
  @Input() formGroup: UntypedFormGroup;

  @Output() cancelEvent = new EventEmitter();
  @Output() changeRequestVin = new EventEmitter<ChangeRequest>();
  @Output() valueChangeEvent = new EventEmitter();

  vehicleInfo: NewVehicle;
  currentVin: string;
  isReplaceVehicleFlow: boolean;
  primaryButtonName = 'Submit';

  private stop$ = new Subject<void>();

  builder = inject(FormBuilder);
  location = inject(Location);
  copyService = inject(CopyService);
  modalService = inject(DsModalService);
  analyticsFacade = inject(AnalyticsFacade);
  vehicleService = inject(VehicleService);
  vehicleFacade = inject(VehicleFacade);
  replaceVehicleService = inject(ReplaceVehicleService);
  quickQuoteFeatureFacade = inject(QuickQuoteFeatureFacade);
  router = inject(Router);
  store = inject(Store);

  agentsModal = 'agentsModal';
  states: State[] = this.copyService.getCopy('shared.usStates');
  vinChangeConfig = this.copyService.getCopy('policy.vinChange');
  vehicleId: string = this.location.getState()['vehicleId'];
  vehicleYear: string = this.location.getState()['vehicleYear'];
  flowOptions: Option[] = this.vinChangeConfig.options;
  vinForm = this.builder.group({
    newVin: new FormControl(
      '',
      Validators.compose([
        ValidationService.vinFormatValidator,
        ValidationService.vinRequiredValidator
      ])
    ),
    flow: new FormControl<Option>(this.flowOptions[0], Validators.required),
    year: new FormControl(this.vehicleYear ? this.vehicleYear : '', Validators.required),
    vehicle: new FormControl(this.vehicleId ? String(this.vehicleId) : '', Validators.required),
    make: new FormControl('', Validators.required),
    model: new FormControl('', Validators.required),
    series: new FormControl('', Validators.required),
    reason: new FormControl('')
  });
  msrp$: Observable<boolean> = this.showMsrp();

  public get yearControl(): AbstractControl<string> {
    return this.vinForm.get('year');
  }

  public get flowControl(): AbstractControl<Option> {
    return this.vinForm.get('flow');
  }

  public get vehiclesControl(): AbstractControl<string> {
    return this.vinForm.get('vehicle');
  }

  public get vinControl(): AbstractControl<string> {
    return this.vinForm.get('newVin');
  }

  public get makeControl(): AbstractControl {
    return this.vinForm.get('make');
  }

  public get modelControl(): AbstractControl {
    return this.vinForm.get('model');
  }

  public get seriesControl(): AbstractControl {
    return this.vinForm.get('series');
  }

  public get reason(): AbstractControl {
    return this.vinForm.get('reason');
  }

  get valid(): boolean {
    return this.vinForm.valid;
  }

  get currentYear(): number {
    return new Date().getFullYear();
  }

  getIsReplaceVehicleFlow() {
    return this.vinForm.valueChanges.pipe(map(v => v.flow.id === VinChangeFlow.REPLACE_VEHICLE));
  }

  transformYear(year: number) {
    return year >= this.currentYear ? this.currentYear : year;
  }

  validateVin(
    vin: string,
    year: string
  ): (formGroup: FormGroup) => Observable<ValidationErrors | null> {
    return (formGroup: FormGroup): Observable<ValidationErrors | null> => {
      const vinControl = formGroup.controls[vin];
      const yearControl = formGroup.controls[year];

      return this.vehicleService
        .getVehicleByVinAndYear(
          {
            year: String(yearControl.value),
            vin: vinControl.value
          },
          false
        )
        .pipe(
          map(vehicle => {
            if (vehicle?.newVehicle) {
              this.vehicleInfo = vehicle.newVehicle;
            }
            return null;
          }),
          catchError(err => {
            this.setVinError(err.status?.errors);
            this.vehicleInfo = null;
            return of({ invalidVin: true });
          })
        );
    };
  }

  closeModal() {
    this.modalService.close(this.agentsModal);
  }

  ngOnInit() {
    this.analyticsFacade.trackEvent({ event: this.vinChangeConfig.analytics.start });

    this.vehiclesControl.valueChanges
      .pipe(
        takeUntil(this.stop$),
        distinctUntilChanged(),
        tap(vehicleId => {
          this.setCurrentVin(vehicleId);
          this.setYear(vehicleId);
          this.controlFlow(+this.flowControl.value.id);
        })
      )
      .subscribe();

    this.flowControl.valueChanges
      .pipe(
        takeUntil(this.stop$),
        map(flow => flow.id),
        distinctUntilChanged(),
        tap(id => {
          this.controlFlow(+id);
        })
      )
      .subscribe();

    this.msrp$
      .pipe(
        takeUntil(this.stop$),
        tap(msrp => {
          if (msrp) {
            const analytics = this.vinChangeConfig.analytics;
            this.analyticsFacade.trackEvent({ event: analytics.notFound });
            this.loadMakes();
          } else {
          }
        })
      )
      .subscribe();
  }

  removeValidators() {
    this.makeControl.removeValidators(Validators.required);
    this.modelControl.removeValidators(Validators.required);
    this.seriesControl.removeValidators(Validators.required);
  }

  setCurrentVin(vehicleId: string) {
    const vehicle = this.vehicles.find(v => v.id === vehicleId);
    this.currentVin = vehicle?.vin;
  }

  setYear(vehicleId: string) {
    const vehicle = this.vehicles.find(v => v.id === vehicleId);
    this.yearControl.setValue(vehicle.year);
  }

  controlFlow(flow: number) {
    if (flow === VinChangeFlow.VIN || !this.eligibleForReplaceVehicle()) this.prepareForVin();
    else this.prepareForReplaceVehicle();
  }

  prepareForVin() {
    this.isReplaceVehicleFlow = false;
    this.primaryButtonName = this.vinChangeConfig.submit;
    if (!this.vinForm.contains('year'))
      this.vinForm.addControl('year', new FormControl(this.vehicleYear, Validators.required));
    if (!this.vinForm.contains('newVin'))
      this.vinForm.addControl(
        'newVin',
        new FormControl(
          '',
          Validators.compose([
            ValidationService.vinFormatValidator,
            ValidationService.vinRequiredValidator
          ])
        )
      );
    this.addValidators();

    this.vinForm.updateValueAndValidity();
  }

  prepareForReplaceVehicle() {
    this.isReplaceVehicleFlow = true;
    this.primaryButtonName = this.vinChangeConfig.next;
    if (this.eligibleForReplaceVehicle()) {
      this.clearValidators();
    }
  }

  addValidators() {
    this.yearControl.addValidators(Validators.required);
    this.yearControl.updateValueAndValidity();
    this.vinControl.addValidators([
      ValidationService.vinRequiredValidator,
      ValidationService.vinFormatValidator
    ]);
    this.vinControl.updateValueAndValidity();
    this.makeControl?.addValidators(Validators.required);
    this.makeControl.updateValueAndValidity();
    this.modelControl?.addValidators(Validators.required);
    this.modelControl.updateValueAndValidity();
    this.seriesControl?.addValidators(Validators.required);
    this.seriesControl.updateValueAndValidity();
  }

  clearValidators() {
    this.yearControl.clearValidators();
    this.yearControl.updateValueAndValidity();
    this.vinControl.clearValidators();
    this.vinControl.updateValueAndValidity();
    this.makeControl?.clearValidators();
    this.makeControl.updateValueAndValidity();
    this.modelControl?.clearValidators();
    this.modelControl.updateValueAndValidity();
    this.seriesControl?.clearValidators();
    this.seriesControl.updateValueAndValidity();
  }

  loadMakes() {
    const year = this.transformYear(+this.yearControl.value);
    this.vehicleFacade.loadMakes(String(year));
  }

  loadModels() {
    const year = this.transformYear(+this.yearControl.value);
    const make = this.makeControl.value;
    this.vehicleFacade.loadModels(String(year), make);
  }

  loadSeries() {
    const year = this.transformYear(+this.yearControl.value);
    const make = this.makeControl.value;
    const model = this.modelControl.value;
    this.vehicleFacade.loadSeries(String(year), make, model);
  }

  eligibleForReplaceVehicle() {
    const vehicleId = this.vehiclesControl.value;
    const vehicle = this.vehicles.find(v => v.id === vehicleId);
    if (vehicle) return this.replaceVehicleService.isEligibaleForReplaceVehicle(vehicle);
    return false;
  }

  hasErrorOtherThanInvalidVin() {
    const errors = this.vinControl.errors;
    let hasOtherError = false;
    for (const error in errors) {
      if (!error.includes('invalidVin')) {
        hasOtherError = true;
        return;
      }
    }
    return hasOtherError;
  }

  showMsrp() {
    return combineLatest([this.vinControl.valueChanges, this.yearControl.valueChanges]).pipe(
      filter(
        ([vin, year]) =>
          !!vin && !!year && this.yearControl.valid && !this.hasErrorOtherThanInvalidVin()
      ),
      switchMap(([vin, year]) =>
        this.vehicleService
          .getVehicleByVinAndYear(
            {
              year: String(year),
              vin
            },
            false
          )
          .pipe(
            tap(vehicle => {
              if (vehicle?.newVehicle) {
                this.vehicleInfo = vehicle.newVehicle;
                this.onValidVin();
              } else this.vehicleInfo = null;
            }),
            map(vehicle => (vehicle ? 1 : 0)),
            catchError(err => {
              this.onInvalidVin();
              this.setVinError(err.status?.errors);
              this.vehicleInfo = null;
              return of(-1); // TODO: confirm with vivek
            })
          )
      ),
      map(success => {
        if (success !== -1) this.removeVinError();
        const year = this.yearControl.value;
        return success === 0 && +year >= new Date().getFullYear();
      }),
      catchError(err => {
        this.onInvalidVin();
        //this.setVinError(err.status?.errors);
        this.vehicleInfo = null;
        return of(false);
      })
    );
  }

  onValidVin() {
    this.makeControl?.clearValidators();
    this.makeControl.updateValueAndValidity();
    this.modelControl?.clearValidators();
    this.modelControl.updateValueAndValidity();
    this.seriesControl?.clearValidators();
    this.seriesControl.updateValueAndValidity();
  }

  onInvalidVin() {
    this.makeControl?.addValidators(Validators.required);
    this.makeControl.updateValueAndValidity();
    this.modelControl?.addValidators(Validators.required);
    this.modelControl.updateValueAndValidity();
    this.seriesControl?.addValidators(Validators.required);
    this.seriesControl.updateValueAndValidity();
  }

  setVinError(errors: any[]) {
    const invalidVin = errors.length > 0;
    if (invalidVin) {
      this.vinForm.get('newVin').setErrors({ invalidVIN: true });
      this.vinForm.get('newVin').markAsTouched({ emitEvent: false });
    }
  }

  removeVinError() {
    this.vinControl.setErrors(null);
  }

  changesForPartner() {
    if (this.isPartner) {
      this.vinForm.removeControl('flow');
    }
  }

  sendToAgent(agent: AgentInfo) {
    this.closeModal();
    const data = this.vinForm.getRawValue();
    const vehicleId = data.vehicle;
    const vehicle = this.vehicles.find(v => v.id === vehicleId);

    const changeRequest = new ChangeRequest();
    changeRequest.policyChangeType = PolicyChangeRequestType.VIN_CHANGE;

    const requestChange: RequestChange = {
      agentId: agent.agentId,
      agentName: agent.name,
      agencyEmail: agent.agencyEmails?.at(0),
      agentEmail: agent.emails?.at(0),
      customerFirstName: this.vinInfo.customerFirstName,
      customerName: this.vinInfo.customerName,
      emailAddress: this.vinInfo.emailAddress,
      mailingAddress: this.vinInfo.mailingAddress,
      customerId: this.vinInfo.customerId,
      phoneNumbers: this.vinInfo.phoneNumbers,
      policyNumbers: this.vinInfo.policyNumbers,
      partnerId: this.vinInfo.partnerId
    };

    const vinChange: VinChange = {
      // agentId: agent.agentId,
      //agentName: agent.name,
      //customerId: this.vinInfo.customerId,
      //partnerId: this.vinInfo.partnerId,
      //customerFirstName: this.vinInfo.customerFirstName,
      //customerName: this.vinInfo.customerName,
      //emailAddress: this.vinInfo.emailAddress,
      //mailingAddress: this.vinInfo.mailingAddress,
      //phoneNumbers: this.vinInfo.phoneNumbers,
      //policyNumbers: this.vinInfo.policyNumbers,
      whyUpdateVin: data.flow.text,
      vehicle: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
      currentVin: vehicle.vin,
      currentYear: +vehicle.year,
      newVin: data.newVin,
      newYear: +data.year,
      reason: data.reason || 'N/A'
    };

    if (data['make']) vinChange.make = data['make'];
    if (data['model']) vinChange.model = data['model'];
    if (data['series']) vinChange.series = data['series'];

    changeRequest.requestChange = requestChange;
    changeRequest.vinChange = vinChange;

    this.changeRequestVin.emit(changeRequest);
  }

  public onSubmit() {
    if (this.isReplaceVehicleFlow) {
      this.navigateToReplaceVehicle();
      return;
    }

    if (this.agents.length > 1) this.modalService.open(this.agentsModal);
    else this.sendToAgent(this.agents[0]);
  }

  navigateToReplaceVehicle() {
    const vehicleId = this.vehiclesControl.value;
    const vehicle = this.vehicles.find(v => v.id === vehicleId);
    this.vehicleFacade.setVehiclePolicyChangeEntryPoint(VechiclePolicyChangeEntryPoint.POLICY);
    this.store.dispatch(new SetVehicleRiskSelectionContext(RiskSelectionContext.REPLACE_VEHICLE));
    this.store.dispatch(
      new ReplaceVehiclePolicySelectAction({
        vehicleId: vehicleId,
        policyNumber: vehicle.policyNumber,
        effectiveDate: vehicle.periodEndDate
      })
    );
    this.quickQuoteFeatureFacade.setVehicleId(vehicleId);
    this.router.navigateByUrl(template(this.vinChangeConfig.replaceVehicleUrl)({ vehicleId }));
  }

  public cancel(): void {
    this.cancelEvent.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.vehicleId) this.setCurrentVin(this.vehicleId);
  }

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }
}

enum VinChangeFlow {
  VIN = 1,
  REPLACE_VEHICLE = 2
}
